import { useEffect, useState } from "react";
import { ChatIcon, ChevronDownIcon } from "@/components/icons";
import { apiClient } from "@/services/apiClient";

interface Note {
  id: string;
  content: string;
  createdAt: string;
}

interface PostClientNotesProps {
  clientId: string;
  postId: string;
}

/**
 * Inline list of notes the client left on a post. Rendered below the
 * caption on admin post cards. Always fetches on mount so notes added
 * after the post list was loaded still appear without a page refresh.
 */
export function PostClientNotes({ clientId, postId }: PostClientNotesProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiClient.get<{ success: boolean; notes: Note[] }>(
          `/client/${clientId}/posts/${postId}/notes`,
        );
        if (!cancelled) setNotes(data.notes || []);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load client notes:", err);
          setError("Impossibile caricare le note");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [clientId, postId]);

  if (notes.length === 0 && !loading && !error) return null;

  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-1.5 text-left"
      >
        <ChatIcon className="h-3.5 w-3.5 text-amber-600" />
        <span className="text-xs font-medium text-amber-700">
          Note dal cliente{notes.length > 0 ? ` (${notes.length})` : ""}
        </span>
        <ChevronDownIcon
          className={`ml-auto h-3.5 w-3.5 text-amber-600 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && loading && notes.length === 0 && (
        <div className="mt-1.5 text-xs text-amber-700/70">Caricamento…</div>
      )}

      {isOpen && error && (
        <div className="mt-1.5 text-xs text-red-600">{error}</div>
      )}

      {isOpen && notes.length > 0 && (
        <ul className="mt-1.5 space-y-1.5">
          {notes.map((note) => (
            <li
              key={note.id}
              className="rounded-md bg-white/70 p-1.5 text-xs text-gray-800"
            >
              <div className="whitespace-pre-wrap break-words">{note.content}</div>
              {note.createdAt && (
                <div className="mt-1 text-[10px] text-gray-500">
                  {new Date(note.createdAt).toLocaleString("it-IT", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
